import { Button } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { DataTable } from '@/components/data-table/data-table';
import { COIN_FEES_QUERY_KEY } from '@/hooks/queries/fees/use-fees';
import { type CoinFee, type FeeType, stripAllZeroFraction } from '@/services/api/fees';
import { formatEuros } from '@/utils/format';
import { CoinFeeEditDialog } from './coin-fee-edit-dialog';
import { FeesErrorBanner } from './fees-error-banner';

const EMPTY_MESSAGE = 'No fees data available';

interface FeeCellProps {
  coinFee: CoinFee;
  feeType: FeeType;
  onEdit: (coinFee: CoinFee, feeType: FeeType) => void;
}

/** Two-line fee cell: EUR value, then the muted native-unit amount. */
const FeeCell = ({ coinFee, feeType, onEdit }: FeeCellProps) => {
  const isDeposit = feeType === 'deposit';
  const eurValue = isDeposit ? coinFee.depositFeeEur : coinFee.withdrawalFeeEur;
  const nativeValue = isDeposit ? coinFee.depositFee : coinFee.withdrawalFee;
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex flex-col">
        <span className="tabular-nums">{formatEuros(eurValue)}</span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {stripAllZeroFraction(nativeValue)} {coinFee.coin.toUpperCase()}
        </span>
      </div>
      {/* Deposit fees are not editable from BOP — the pencil is
          rendered but disabled; only withdrawal fees post withdrawalFeeEur. */}
      <Button
        aria-label={`Edit ${coinFee.coin.toUpperCase()} ${feeType} fee`}
        disabled={isDeposit}
        onClick={() => onEdit(coinFee, feeType)}
        size="icon"
        type="button"
        variant="ghost"
      >
        <Pencil className="size-4" />
      </Button>
    </div>
  );
};
FeeCell.displayName = 'FeeCell';

interface EditTarget {
  coinFee: CoinFee;
  feeType: FeeType;
}

export interface CoinFeesPanelProps {
  fees: CoinFee[] | undefined;
  isLoading: boolean;
  errorMessage: string | null;
  onRetry: () => void;
}

/** Fiat/Crypto fees table — one instance per tab over the split dataset. */
export const CoinFeesPanel = ({ fees, isLoading, errorMessage, onRetry }: CoinFeesPanelProps) => {
  const queryClient = useQueryClient();
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);

  const handleEdit = useCallback((coinFee: CoinFee, feeType: FeeType) => setEditTarget({ coinFee, feeType }), []);

  const columns = useMemo(
    (): ColumnDef<CoinFee, unknown>[] => [
      {
        accessorKey: 'coin',
        header: 'Coin',
        cell: ({ row }) => <span className="font-medium">{row.original.coin.toUpperCase()}</span>,
      },
      {
        id: 'depositFee',
        accessorKey: 'depositFeeEur',
        header: 'Deposit Fee',
        cell: ({ row }) => <FeeCell coinFee={row.original} feeType="deposit" onEdit={handleEdit} />,
      },
      {
        id: 'withdrawalFee',
        accessorKey: 'withdrawalFeeEur',
        header: 'Withdrawal Fee',
        cell: ({ row }) => <FeeCell coinFee={row.original} feeType="withdrawal" onEdit={handleEdit} />,
      },
    ],
    [handleEdit],
  );

  if (errorMessage) return <FeesErrorBanner message={errorMessage} onRetry={onRetry} />;

  return (
    <div className="max-w-[600px]">
      <DataTable columns={columns} data={fees} emptyMessage={EMPTY_MESSAGE} getRowId={(row) => row.coin} isLoading={isLoading} />
      {editTarget ? (
        <CoinFeeEditDialog
          coinFee={editTarget.coinFee}
          feeType={editTarget.feeType}
          onClose={() => setEditTarget(null)}
          onSubmitted={() => void queryClient.invalidateQueries({ queryKey: COIN_FEES_QUERY_KEY })}
        />
      ) : null}
    </div>
  );
};
CoinFeesPanel.displayName = 'CoinFeesPanel';
